/**
 * `<input type="date">` on one side, ISO-8601 instants on the wire on the other.
 *
 * These three functions started life inside `NewTenantPage`, where one screen
 * owned the only date on the admin panel. Slice 8 adds a second screen that has
 * to write the *same* column — `tenants.valid_till` — and the moment two screens
 * convert a calendar day into an instant, they have to agree about what "that
 * day" means. They would not have: the obvious second implementation is
 * `new Date(value).toISOString()`, which is midnight UTC, and a subscription
 * written that way expires up to a day earlier than the one written by the
 * create form. So the conversion lives here, once.
 *
 * THE CONVERSION, AND ITS COST. A date input yields a local calendar day with
 * no time and no zone. A subscription runs to the *end* of that day, so the day
 * becomes 23:59:59.999 **local time** as an ISO instant. That is the right
 * reading for the person typing it and it has a consequence worth stating
 * rather than discovering: the instant depends on the browser's timezone, so
 * staff in two zones who type the same day produce two different instants. The
 * detail screen therefore prints the resulting instant in full beside the
 * input, instead of leaving the day to imply it.
 *
 * WHY {@link isSameCalendarDay} EXISTS. The edit screen has to decide whether
 * the operator moved the expiry, and the only honest comparison is between two
 * *day strings*. Comparing the ISO instants would report a change every time a
 * tenant whose stored expiry is not exactly 23:59:59.999 local is re-saved —
 * every tenant created in another timezone, and every tenant whose expiry was
 * set by anything other than this form — which would write an audit row saying
 * a date moved when nobody touched it.
 */

/** `yyyy-mm-dd` in the viewer's own timezone. */
function localDayString(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${month}-${day}`;
}

/** `yyyy-mm-dd` for today, local time — the earliest sensible expiry. */
export function todayInputValue(): string {
  return localDayString(new Date());
}

/**
 * An ISO instant as the `yyyy-mm-dd` a date input can hold, in local time.
 * Empty string for anything unparseable, which renders as an empty input rather
 * than as `Invalid Date`.
 */
export function toDateInputValue(iso: string): string {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? '' : localDayString(new Date(time));
}

/**
 * A `yyyy-mm-dd` from a date input as the end of that day, local time.
 * `undefined` for an empty or unparseable value, so the caller omits the field
 * rather than sending a null it did not mean.
 */
export function toIsoEndOfDay(value: string): string | undefined {
  if (value === '') return undefined;
  const parsed = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/**
 * Whether an ISO instant falls on the calendar day a date input is holding.
 * The comparison the edit form uses to decide the expiry moved — see the header.
 */
export function isSameCalendarDay(iso: string, dayValue: string): boolean {
  return toDateInputValue(iso) === dayValue;
}
